import { Component, inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { CheckoutPaymentMethodTypes } from 'src/app/+checkout-purchase/components/checkout-payment-methods.component/enums';
import { CheckoutState } from 'src/app/model/enums/checkout-state';
import { StorageKeys } from 'src/app/model/enums/storage-keys';
import { Fee } from 'src/app/model/interfaces/fee';
import { ReceiptOrders } from 'src/app/model/interfaces/receipt-orders';
import { OrderService, PlayerService, StorageService } from 'src/app/services';
import { PriceService } from 'src/app/services/price.service';
import { ReceiptService } from 'src/app/services/receipt-service';
import { TrackingService } from 'src/app/services/tracking.service';
import { TrackingFacadeService } from 'src/app/services/tracking/tracking-facade.service';
import { MembershipState } from '../../../model/enums/membership-state';
import { PaymentMethodType } from '../../../model/enums/payment-method-type';
import { Order } from '../../../model/interfaces/order';
import { PaymentInterval } from '../../../model/types/payment-interval';
import { LotteryService } from '../../../services/lottery.service';
import { buildGapfundingPromotion } from '../../gapfunding-upsell/gapfunding-upsell.component';

interface SessionStorageData {
  checkoutState: CheckoutState;
  paymentPeriod: number;
  gapfundDeclined: boolean;
  paymentMethodUsed: number;
}

@Component({
    selector: 'app-bs-receipt',
    templateUrl: 'bs-receipt.component.html',
    styleUrls: ['bs-receipt.component.scss']
})

export class BsReceiptComponent implements OnInit, OnDestroy {
  // Essential UI properties
  public orders: ReceiptOrders;
  public order: Order;
  public accordionIsOpen = false;
  public dateOfPurchase = new Date();
  
  // Session storage data
  public checkoutState: CheckoutState;
  public paymentPeriod: number;
  public gapfundDeclined = false;
  public paymentMethodUsed: number;
  
  // Lottery and pricing data
  public dibsSalesDate: Date;
  public bsSalesDate: Date;
  public nextBSDrawDate: Date;
  public pricePrMonth: number;
  public priceTotal: number;
  public classCount: PaymentInterval;
  public fees: Fee[] = [];
  public feePrice: number;
  public totalPriceIncludingFees: number;
  public canOfferGapFunding: boolean = false;
  
  private readonly receiptService = inject(ReceiptService);
  private readonly router = inject(Router);
  private readonly trackingService = inject(TrackingService);
  private readonly storageService = inject(StorageService);
  private readonly priceService = inject(PriceService);
  private readonly lotteryService = inject(LotteryService);
  private readonly trackingFacadeService = inject(TrackingFacadeService);
  private readonly orderService = inject(OrderService);
  private readonly playerService = inject(PlayerService);
  private readonly locale = inject(LOCALE_ID);
  private membershipState: MembershipState;
  
  // Subscription management
  private readonly destroy$ = new Subject<void>();

  public async ngOnInit(): Promise<void> {
    try {
      this.initializeSessionStorageData();
      this.orders = this.receiptService.getOrders();
      this.membershipState = await this.playerService.membershipState(1);
      await this.handleBsPaymentReceipt();
      this.trackPaymentMethodChoice();
    } catch (error) {
      console.error('Error initializing BS receipt:', error);
    }
  }

  private initializeSessionStorageData(): void {
    try {
      const paymethodData = sessionStorage.getItem(StorageKeys.Paymethod);
      this.checkoutState = paymethodData 
        ? JSON.parse(paymethodData).PayMethodState as CheckoutState
        : null;
      
      this.paymentPeriod = Number(sessionStorage.getItem(StorageKeys.PaymentPeriod)) || 0;
      this.gapfundDeclined = Boolean(sessionStorage.getItem(StorageKeys.GapfundDeclined));
      this.paymentMethodUsed = Number(sessionStorage.getItem(StorageKeys.PaymentMethodUsed)) || 0;
    } catch (error) {
      console.error('Error parsing session storage data:', error);
      // Set default values on error
      this.checkoutState = null;
      this.paymentPeriod = 0;
      this.gapfundDeclined = false;
      this.paymentMethodUsed = 0;
    }
  }

  private async handleBsPaymentReceipt(): Promise<void> {
    const order = await this.getOrder();
    if (!order) {
      throw new Error('No order found for BS payment receipt');
    }

    this.order = order;
    this.initializeOrderData(order);
    
    // Load data in parallel for better performance
    await this.loadReceiptData(order);
  }

  private async loadReceiptData(order: Order): Promise<void> {
    try {
      await Promise.all([
        this.loadLotteryClassData(order),
        this.loadPricingData(order),
        // Awaited here (rather than fire-and-forget) so dibsSalesDate is available for the
        // gap-funding promotion attached to the purchase_complete event.
        this.loadLotteryDates()
      ]);
    } catch (error) {
      console.error('Error loading receipt data:', error);
      throw new Error('Failed to load receipt data');
    }
  } 
  
  private initializeOrderData(order: Order): void {
    // Class count is 1 for monthly payment and 6 for semi-annual payment
    this.classCount = order.NumberOfClasses;
  }


  private async loadLotteryClassData(order: Order): Promise<void> {
    const { FMTClassId: firstPossibleDrawClassId, SubscriptionStartClassId: subscriptionStartClassId } = order;

    if (typeof subscriptionStartClassId !== 'number') {
      console.warn('SubscriptionStartClassId is not defined on the order');
      return;
    }

    try {
      const lotteryClass = await firstValueFrom(this.lotteryService.getLotteryClassById(subscriptionStartClassId))
      const [firstDraw] = lotteryClass.Draws;
      this.bsSalesDate = firstDraw.DrawDate;



      // Gap funding allows users to participate in an earlier lottery draw.
      // This is only relevant when subscriptionStartClassId differs from firstPossibleDrawClassId and
      // FMTClassId aka firstPossibleDrawClassId is greater than 0.
      // Meaning there's a gap between when their subscription starts and the first possible draw.
      // If the IDs match, the user is already in the earliest possible class, so gap funding has no benefit.
      this.canOfferGapFunding = firstPossibleDrawClassId > 0 && firstPossibleDrawClassId !== subscriptionStartClassId;
      this.storageService.setItem(StorageKeys.LotteryClassId, firstPossibleDrawClassId);
    } catch (error) {
      console.error('Error loading lottery class data:', error);
      throw error;
    }
  }

  private async loadPricingData(order: Order): Promise<void> {
    try {
      const priceData = await firstValueFrom(
        this.priceService.getPriceByOrder(order).pipe(takeUntil(this.destroy$))
      );
      
      this.pricePrMonth = priceData.pricePrMonth;
      this.priceTotal = priceData.totalPrice;
      
      // Calculate fees
      this.fees = this.priceService.getFeesByOrder(order);
      this.feePrice = this.fees.length > 0 ? this.fees[0].value : 0;
      this.totalPriceIncludingFees = this.fees.length > 0 
        ? priceData.totalPrice + this.fees[0].value 
        : priceData.totalPrice;
    } catch (error) {
      console.error('Error loading pricing data:', error);
      throw error;
    }
  }

  public toggleAccordion(): void {
    this.accordionIsOpen = !this.accordionIsOpen;
  }

  private async loadLotteryDates(): Promise<void> {
    const orders = this.receiptService.getOrders();
    const fmtOrder = orders.orderSubscription;

    if (!fmtOrder) {
      console.warn('No FMT order found for lottery dates');
      return;
    }

    try {
      const lotteryPromises = [];

      // Add DIBS lottery promise if FMTClassId exists
      if (fmtOrder.FMTClassId) {
        lotteryPromises.push(
          this.loadDibsLotteryDate(fmtOrder.FMTClassId)
        );
      }

      // Add BS lottery promise if SubscriptionStartClassId exists
      if (fmtOrder.SubscriptionStartClassId) {
        lotteryPromises.push(
          this.loadBsLotteryDate(fmtOrder.SubscriptionStartClassId)
        );
      }

      // Load all lottery dates in parallel
      if (lotteryPromises.length > 0) {
        await Promise.all(lotteryPromises);
      }
    } catch (error) {
      console.error('Error loading lottery dates:', error);
    }
  }

  private async loadDibsLotteryDate(classId: number): Promise<void> {
    try {
      const lotteryClass = await firstValueFrom(
        this.lotteryService.getLotteryClassById(classId)
          .pipe(takeUntil(this.destroy$))
      );
      
      if (lotteryClass?.Draws?.length) {
        this.dibsSalesDate = new Date(lotteryClass.Draws[0].DrawDate);
      }
    } catch (error) {
      console.error('Error loading DIBS lottery date:', error);
    }
  }

  private async loadBsLotteryDate(classId: number): Promise<void> {
    try {
      const lotteryClass = await firstValueFrom(
        this.lotteryService.getLotteryClassById(classId)
          .pipe(takeUntil(this.destroy$))
      );
      
      if (lotteryClass?.Draws?.length) {
        this.nextBSDrawDate = new Date(lotteryClass.Draws[0].DrawDate);
      }
    } catch (error) {
      console.error('Error loading BS lottery date:', error);
    }
  }

  private async getOrder(): Promise<Order> {
    return this.receiptService.getOrders().orderSubscription;
  }

  public onConfirmCreateGapFund = async (): Promise<void> => {
    // Since "netorder/basket" has no timeout for gap funding,
    // we can safely create an SMT order on the fly.
    // If the user decides to skip the payment, there's no issue.
    const smtOrder = await firstValueFrom(this.orderService.createSmtOrderByOrderId(this.order.Id));
    this.storageService.setItem(StorageKeys.SmtOrderId, smtOrder.Id);
    this.trackingService.gapfundAccept();

    this.router.navigate(['/betaling/spil-med-nu']);
  }

  private trackPaymentMethodChoice(): void {
    const order = this.orders.orderSubscription || this.orders.orderSmt;
    if (!order) {
      console.warn('No order found for payment method tracking');
      return;
    }

    const paymentMethods = this.getPaymentMethodsFromStorage();
    if (paymentMethods.subscription === null) {
      return;
    }

    this.executePaymentTracking(order, paymentMethods);
  }

  private getPaymentMethodsFromStorage() {
    const subscription = this.storageService.getItem(StorageKeys.PaymentMethodUsed);
    const checkoutPaymentMethodType = this.storageService.getItem(StorageKeys.PaymentMethodUsedGapfund);
    
    const gapfund = checkoutPaymentMethodType === CheckoutPaymentMethodTypes.BillwerkApplepay
      ? PaymentMethodType.ApplePay
      : checkoutPaymentMethodType === CheckoutPaymentMethodTypes.MobilePay
        ? PaymentMethodType.MobilePay
        // Keep Vipps recurring distinct from card/mobilepay in receipt tracking.
        : checkoutPaymentMethodType === CheckoutPaymentMethodTypes.VippsRecurring
          ? PaymentMethodType.VippsRecurring
        : PaymentMethodType.Card; 
    
    return {
      subscription,
      gapfund,
    };
  }

  private executePaymentTracking(order: Order, paymentMethods: { subscription: any; gapfund: PaymentMethodType }): void {
    const isBetalingsservice = paymentMethods.subscription === CheckoutPaymentMethodTypes.Betalingsservice;

    if (isBetalingsservice) {
      this.trackingFacadeService.trackOrderOnPaymentFlowPayMethodBS(this.orders.orderSubscription, this.membershipState);
      // The gap-funding upsell is shown only for Betalingsservice purchases where gap funding can be
      // offered. Attach it as a promotion spot to purchase_complete (the page's last event) instead of
      // a separate promotion event — this is where all the data is available.
      const promotion = this.canOfferGapFunding
        ? [buildGapfundingPromotion(this.dibsSalesDate, this.locale)]
        : undefined;
      // Fire the purchase event for Betalingsservice too, so card and BS are symmetric (payment_method = "Betalingsservice").
      this.orderService.trackPurchaseComplete(this.orders.orderSubscription, this.membershipState, PaymentMethodType.Betalingsservice, promotion);
    } else {
      this.orderService.trackPurchaseComplete(order, this.membershipState, paymentMethods.gapfund);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
